import { ReactNode, forwardRef } from 'react';
import {
  Platform,
  Pressable,
  TextInput,
  type TextInputProps,
  View,
} from 'react-native';
import { BodyText } from '@/common/components/BodyText/BodyText';
import { Label } from '@/common/components/Label/Label';
import { styles } from './FloatingField.styles';

export const LabeledInput = forwardRef<
  React.ElementRef<typeof TextInput>,
  ILabeledInputProps
>(
  (
    {
      label,
      labelProps,
      active,
      verticalAlign = 'center',
      horizontalAlign = 'left',
      inline,
      customElement,
      inputProps,
    },
    ref
  ) => {
    const LabelComponent = Platform.OS === 'web' ? 'label' : View;
    return (
      <View className={styles.inputWrapper}>
        {!inline && (
          <LabelComponent
            className={styles.labelWrapper({ align: verticalAlign, active })}
            style={{
              transformOrigin: '0 0',
            }}
            htmlFor={inputProps?.id}
            {...labelProps}
          >
            <Label className={styles.label} color="neutral20" numberOfLines={1}>
              {label}
            </Label>
          </LabelComponent>
        )}
        {customElement ? (
          <View
            className={styles.input({
              align: horizontalAlign,
              className: inputProps?.className,
            })}
          >
            {customElement}
          </View>
        ) : (
          <TextInput
            {...inputProps}
            className={styles.input({
              align: horizontalAlign,
              inline,
              disabled: inputProps?.disabled,
              className: inputProps?.className,
            })}
            blurOnSubmit={false}
            aria-label={inline ? label : undefined}
            ref={ref}
            editable={!inputProps?.disabled}
            allowFontScaling={false}
          />
        )}
      </View>
    );
  }
);
LabeledInput.displayName = 'LabeledInput';

export const FloatingField = ({
  error,
  focused,
  disabled,
  className,
  wrapperClassName,
  hint,
  hintHidden,
  inline,
  noPadding,
  onPress,
  children,
}: IFloatingFieldProps) => {
  const showHint = !hintHidden && (error || hint);
  const showError = !disabled && !!error;

  return (
    <View className={styles.root({ className })}>
      <Pressable
        className={styles.wrapper({
          focused,
          disabled,
          inline,
          noPadding,
          withError: showError,
          className: wrapperClassName,
        })}
        tabIndex={-1}
        onPress={!disabled ? onPress : undefined}
      >
        {children}
      </Pressable>
      {showHint && (
        <FloatingFieldHint invalid={showError}>
          {showError ? error : hint}
        </FloatingFieldHint>
      )}
    </View>
  );
};

export const FloatingFieldHint = ({
  invalid,
  children,
}: {
  invalid?: boolean;
  children: ReactNode;
}) => {
  return (
    <BodyText size="sm" color="neutral20" className={styles.hint({ invalid })}>
      {children}
    </BodyText>
  );
};

export interface IFloatingFieldProps {
  error?: ReactNode;
  focused?: boolean;
  disabled?: boolean;
  className?: string;
  wrapperClassName?: string;
  hint?: ReactNode;
  hintHidden?: boolean;
  onPress?: () => void;
  inline?: boolean;
  noPadding?: boolean;
  children: ReactNode;
}

export interface ILabeledInputProps {
  label: string;
  active?: boolean;
  verticalAlign?: 'top' | 'center';
  horizontalAlign?: 'left' | 'center' | 'right';
  inline?: boolean;
  customElement?: ReactNode;
  inputProps?: TextInputProps;
  labelProps?: object;
}
